import MDBox from 'components/MDBox'
import { useAppServices } from 'hook/services'
import { useEffect, useState, useRef } from 'react'
import toast from 'react-hot-toast'

export default function useAppData() {
  const AppService = useAppServices()
  const [list, setList] = useState([])
  const [loader, setLoader] = useState(true)
  const [search, setSearch] = useState('')
  const typingTimeoutRef = useRef(null)

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalUsers: 0,
  })

  const getFiles = async (page = 1, limit = 10, searchTerm = search) => {
    setLoader(true)
    try {
      const params = {
        page,
        limit,
        toaster: false,
        search: searchTerm?.trim() || '',
      }

      const query = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&')

      const { response } = await AppService.files.get({ query })

      if (response?.success) {
        setList(response.data || [])
        setPagination({
          page: response.pagination?.currentPage || page,
          limit: response.pagination?.usersPerPage || limit,
          totalPages: response.pagination?.totalPages || 0,
          totalUsers: response.pagination?.totalUsers || 0,
        })
      } else {
        setList([])
      }
    } catch (err) {
      console.error('❌ Error fetching files:', err)
      setList([])
    } finally {
      setLoader(false)
    }
  }

  const onLoad = () => {
    getFiles(pagination.page, pagination.limit)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return
    try {

      const { response, error } = await AppService.files.delete({ query: `id=${id}` })
      if (response?.success) {
        toast.success('File deleted successfully!')
        getFiles(pagination.page, pagination.limit)
      } else {
        toast.error(response?.message || 'Failed to delete file')
        console.error('Delete failed:', error)
      }
    } catch (err) {
      console.error('Error deleting file:', err)
      toast.error('Something went wrong while deleting file')
    }
  }

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url)
    toast.success('URL copied to clipboard!')
  }

  useEffect(() => {
    getFiles(1, pagination.limit)
  }, [])

  useEffect(() => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => {
      getFiles(1, pagination.limit, search)
    }, 1000)
    return () => clearTimeout(typingTimeoutRef.current)
  }, [search])

  const handlePageChange = (newPage) => {
    getFiles(newPage, pagination.limit, search)
  }

  const handleLimitChange = (newLimit) => {
    getFiles(1, newLimit, search)
  }

  const handleSearch = () => {
    getFiles(1, pagination.limit, search)
  }

  const formatBytes = (bytes) => {
    if (!bytes) return '—'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  return {
    loader,
    handleRefresh: onLoad,
    pagination,
    handlePageChange,
    handleLimitChange,
    search,
    setSearch,
    handleSearch,
    dataTableData: {
      columns: [
        { Header: 'Preview', accessor: 'preview' },
        { Header: 'Filename', accessor: 'filename' },
        { Header: 'Type', accessor: 'mimetype' },
        { Header: 'Size', accessor: 'size' },
        { Header: 'Uploaded', accessor: 'createdAt' },
        { Header: 'Action', accessor: 'action' },
      ],
      rows: list.map((data) => ({
        preview: (
          <img
            src={data?.fullUrl}
            alt={data?.filename}
            style={{
              width: 50,
              height: 50,
              objectFit: 'cover',
              borderRadius: 6,
              border: '1px solid #e0e0e0',
            }}
            onError={(e) => {
              e.target.style.display = 'none'
            }}
          />
        ),
        filename: (
          <span
            title={data?.filename}
            style={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {data?.filename || '—'}
          </span>
        ),
        mimetype: (
          <span
            style={{
              background: '#e3f2fd',
              color: '#1565c0',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {data?.mimetype?.split('/')[1]?.toUpperCase() || '—'}
          </span>
        ),
        size: formatBytes(data?.size),
        createdAt: formatDate(data?.createdAt),
        action: (
          <MDBox display="flex" gap={1} flexWrap="wrap">
            {/* Copy URL */}
            <button
              onClick={() => handleCopyUrl(data?.fullUrl)}
              title="Copy URL"
              style={{
                background: '#1976d2',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Copy URL
            </button>

            {/* View */}
            <a
              href={data?.fullUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                background: '#388e3c',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              View
            </a>

            {/* Delete */}
            <button
              onClick={() => handleDelete(data._id)}
              style={{
                background: '#DC2626',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Delete
            </button>
          </MDBox>
        ),
      })),
    },
  }
}